import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Label } from "@/Components/ui/label";
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import { Separator } from "@/Components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/Components/ui/accordion";
import { Calendar as CalendarIcon, X, Trees, PlusCircle, Search, ArrowLeft, CreditCard, Apple } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/Components/ui/select";
import { Field } from "@/types/field";
import { User } from "@/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/Components/ui/dialog";
import { Checkbox } from "@/Components/ui/checkbox";
import { Calendar } from "@/Components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/Components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import axios from 'axios';
import SlotSelector from "@/Components/Built/Website/Booking/SlotSelector";
import { PaymentMethod } from '@/types';
import { Alert, AlertDescription } from "@/Components/ui/alert";
import { CardElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import MultiPenSlotSelector from "@/Components/Built/Website/Booking/MultiPenSlotSelector";
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { loadStripe } from '@stripe/stripe-js';
import PaymentProcessingDialog from '@/Components/Built/Website/PaymentProcessingDialog';
import { set } from 'lodash';
import DatePicker, { DateAvailability as CalendarDateAvailability } from "@/Components/Built/Website/DatePicker";

const stripePromise = loadStripe('pk_live_51IssiHJvc5NxDsL0D549eawGC7d7F1yXD4J6kIefnliJ72HgqLLprBwXGhwc5bGh1XC5V7GWx5O7pAEjsmP4AjNd00ZNXEk8KL');

interface Breed {
    id: number;
    name: string;
    hash: string;
    banned: number;
}

interface Dog {
    name: string;
    breed: string;
}

interface BookingFormProps {
    user: User | null;
    field: Field;
    mapboxToken: string;
}

interface Address {
    line1: string;
    line2: string;
    city: string;
    county: string;
    postcode: string;
    country: string;
}

interface BookingSummary {
    booking_date: string;
    booking_start_time: string;
    booking_length: number;
    number_of_dogs: number;
    discount?: number;
    original_price?: number;
    price: number;
    sessions_to_pay: number;
    per_session_price: number;
    card_balance?: number;
    has_multiple?: number;
    problem_sessions?: number;
    rewards_used?: number;
    card_used?: number;
}

interface AdditionalBooking {
    date: Date;
    slotLength: string;
    selectedSlots: number[];
    penId: string;
}

interface DateAvailability {
    date: string;
    availability: 'plenty' | 'limited' | 'few' | 'none';
    available_slots: number;
    total_slots: number;
}

// Define the schema for form validation
const schema = z.object({
    fullName: z.string().min(1, "Full name is required"),
    phoneNumber: z.string().min(1, "Phone number is required"),
    email: z.string().email("Invalid email address"),
    numberOfDogs: z.string().min(1, "Number of dogs is required"),
    dogs: z.array(z.object({
        name: z.string().min(1, "Dog name is required"),
        breed: z.string().min(1, "Dog breed is required"),
    })).optional(),
    dogsCertification: z.boolean().refine((val) => val === true, {
        message: "You must confirm that all dogs have been wormed, microchipped and vaccinated",
    }),
    penId: z.string().min(1, "Please select a pen"),
    address: z.object({
        line1: z.string().min(1, "Address line 1 is required"),
        line2: z.string().optional(),
        city: z.string().min(1, "City is required"),
        county: z.string().min(1, "County is required"),
        postcode: z.string().min(1, "Postcode is required"),
        country: z.string().min(1, "Country is required"),
    }),
    bookingDate: z.date({
        required_error: "A booking date is required.",
    }),
    bookingLength: z.string({
        required_error: "Please select a booking length.",
    }),
    selectedSlots: z.array(z.number()).min(1, "Please select at least one slot."),
    paymentType: z.enum(['card', 'wallet', 'saved_method']).optional(),
    paymentMethodId: z.string().optional(),
    stripePaymentIntentId: z.string().optional(),
    discountCode: z.string().optional(),
    isRecurring: z.boolean().optional(),
    recurringDuration: z.string().optional(),
    useRewardsBalance: z.boolean().optional(),
    termsAccepted: z.boolean().refine((val) => val === true, {
        message: "You must accept the terms and conditions",
    }),
    giftCardNumber: z.string().optional(),
    newsletter: z.boolean().optional()
});

type FormData = z.infer<typeof schema>;

const PaymentForm: React.FC<{
    onPaymentComplete: (paymentIntentId: string) => void;
    bookingReference: string;
}> = ({ onPaymentComplete, bookingReference }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);

    const handlePayment = async () => {
        if (!stripe || !elements) {
            setPaymentError("Payment cannot be processed at this time.");
            return;
        }

        setIsProcessing(true);
        setPaymentError(null);

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/booking/${bookingReference}/thanks`,
            },
            redirect: 'if_required',
        });

        if (error) {
            setPaymentError(error.message || "An error occurred during payment.");
            setIsProcessing(false);
        } else if (paymentIntent && paymentIntent.status === "succeeded") {
            onPaymentComplete(paymentIntent.id);
        } else {
            setPaymentError("Unexpected payment result. Please try again.");
            setIsProcessing(false);
        }
    };

    return (
        <>
            <PaymentElement options={{ layout: { type: 'accordion', radios: false } }}/>
            {paymentError && (
                <Alert variant="destructive" className="mt-4">
                    <AlertDescription>{paymentError}</AlertDescription>
                </Alert>
            )}
            <DialogFooter>
                <Button onClick={handlePayment} disabled={isProcessing}>
                    {isProcessing ? 'Processing...' : 'Pay & Confirm Order'}
                </Button>
            </DialogFooter>
        </>
    );
};

const CountdownTimer: React.FC<{ minutes: number; onExpire: () => void }> = ({ minutes, onExpire }) => {
    const [seconds, setSeconds] = useState(minutes * 60);

    useEffect(() => {
        const interval = setInterval(() => {
            setSeconds(prevSeconds => {
                if (prevSeconds <= 1) {
                    clearInterval(interval);
                    onExpire();
                    return 0;
                }
                return prevSeconds - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [minutes, onExpire]);

    const formatTime = (time: number) => time.toString().padStart(2, '0');

    const minutesLeft = Math.floor(seconds / 60);
    const secondsLeft = seconds % 60;

    return (
        <div className="text-xl font-bold">
            Time remaining: {formatTime(minutesLeft)}:{formatTime(secondsLeft)}
        </div>
    );
};

const PaymentDialog: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    clientSecret: string;
    onPaymentComplete: (paymentIntentId: string) => void;
    bookingReference: string;
}> = ({ isOpen, onClose, clientSecret, onPaymentComplete, bookingReference }) => {
    const handleExpire = () => {
        onClose();
        // Additional logic for when the timer expires (e.g., show an alert)
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px] max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Complete Your Payment</DialogTitle>
                </DialogHeader>
                <div className="flex-grow overflow-y-auto pr-4 -mr-4">
                    <Alert>
                        <AlertDescription className='bg-yellow-100 p-4 mb-2'>
                            This order is not yet complete. You have 30 minutes to pay for this order, or it will be cancelled and your slots released for others.
                        </AlertDescription>
                    </Alert>
                    <div className='mt-6'>
                        <CountdownTimer minutes={30} onExpire={handleExpire} />
                    </div>
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                        <PaymentForm
                            onPaymentComplete={onPaymentComplete}
                            bookingReference={bookingReference}
                        />
                    </Elements>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const DogInfoForm: React.FC<{
    dogs: Dog[],
    setDogs: React.Dispatch<React.SetStateAction<Dog[]>>,
    breeds: Breed[],
    setCertification: React.Dispatch<React.SetStateAction<boolean>>,
    onSave: () => void,
}> = ({ dogs, setDogs, breeds, setCertification, onSave }) => {
    const handleDogChange = (index: number, field: keyof Dog, value: string) => {
        const newDogs = [...dogs];
        newDogs[index] = { ...newDogs[index], [field]: value };
        setDogs(newDogs);
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                {dogs.map((dog, index) => (
                    <div key={index} className="space-y-2">
                        <h4 className="font-semibold text-lg">Dog {index + 1}</h4>
                        <div>
                            <Label htmlFor={`dog-name-${index}`}>Name</Label>
                            <Input
                                id={`dog-name-${index}`}
                                placeholder="Dog Name"
                                value={dog.name}
                                onChange={(e) => handleDogChange(index, 'name', e.target.value)}
                            />
                        </div>
                        <div>
                            <Label htmlFor={`dog-breed-${index}`}>Breed</Label>
                            <Select
                                value={dog.breed}
                                onValueChange={(value) => handleDogChange(index, 'breed', value)}
                            >
                                <SelectTrigger id={`dog-breed-${index}`}>
                                    <SelectValue placeholder="Select Breed" />
                                </SelectTrigger>
                                <SelectContent>
                                    {breeds.map((breed) => (
                                        <SelectItem key={breed.id} value={breed.id.toString()}>
                                            {breed.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                ))}
            </div>
            <Separator className="my-4" />
            <DialogFooter>
                <Button onClick={onSave}>Save</Button>
            </DialogFooter>
        </div>
    );
};

const BannedBreedDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Important Message</DialogTitle>
                </DialogHeader>
                <div className="p-4 bg-blue-200 rounded">
                    Due to the sensitivities around this breed, we ask you to keep your dog muzzled and on a lead while in the communal parking area. They can be unmuzzled and let off the lead once in the security of the field.
                </div>
                <DialogFooter>
                    <Button onClick={onClose}>Okay</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const NewBookingForm: React.FC<BookingFormProps> = ({ field, user, mapboxToken }) => {
    const [breeds, setBreeds] = useState<Breed[]>([]);
    const [dogs, setDogs] = useState<Dog[]>([{ name: '', breed: '' }]);
    const [dogsCertification, setDogsCertification] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [bannedBreedDialogOpen, setBannedBreedDialogOpen] = useState(false);
    const [selectedPenId, setSelectedPenId] = useState<string | null>(null);
    const [calendarOpen, setCalendarOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [isManualEntry, setIsManualEntry] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [selectedSlots, setSelectedSlots] = useState<number[]>([]);

    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);

    const [bookingSummary, setBookingSummary] = useState<BookingSummary | null>(null);
    const [isFetchingPrice, setIsFetchingPrice] = useState(false);
    const [bookingError, setBookingError] = useState<string | null>(null);

    const [isAddingCard, setIsAddingCard] = useState(false);
    const [newCardholderName, setNewCardholderName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [setupIntentSecret, setSetupIntentSecret] = useState<string>('');
    const [isLoadingIntent, setIsLoadingIntent] = useState(false);

    const [isMakingBooking, setIsMakingBooking] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);

    const [showAddCardForm, setShowAddCardForm] = useState(false);

    const [isCardAdded, setIsCardAdded] = useState(false);
    const [processingBooking, setProcessingBooking] = useState(false);

    const [paymentMessage, setPaymentMessage] = useState<string>('');
    const [paymentType, setPaymentType] = useState<string>('');

    const [payOnceButton, setPayOnceButton] = useState(true);

    const [discountCode, setDiscountCode] = useState('');
    const [discountError, setDiscountError] = useState<string | null>(null);
    const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
    const [isDiscountApplied, setIsDiscountApplied] = useState(false);

    const [showSavedPaymentMethods, setShowSavedPaymentMethods] = useState(true);

    const [isAnyPenSelected, setIsAnyPenSelected] = useState(false);

    const [isSamsungMobile, setIsSamsungMobile] = useState(false);

    const [postcodeWarning, setPostcodeWarning] = useState('');

    const [isRecurring, setIsRecurring] = useState(false);
    const [isRecurringEnabled, setIsRecurringEnabled] = useState(true);
    const [recurringCycle, setRecurringCycle] = useState<string | null>(null);
    const [recurringFrequency, setRecurringFrequency] = useState<string | null>(null);
    const [recurringSlots, setRecurringSlots] = useState<any[]>([]);
    const [showRecurringDialog, setShowRecurringDialog] = useState(false);

    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

    const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);
    const [alternativeDate, setAlternativeDate] = useState<Date | null>(null);
    const [alternativeSlots, setAlternativeSlots] = useState<number[]>([]);

    const [isRecurringFinalized, setIsRecurringFinalized] = useState(false);
    const [recurringBookingSummary, setRecurringBookingSummary] = useState('');

    const [payAllRecurring, setPayAllRecurring] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [initialPaymentPrice, setInitialPaymentPrice] = useState(0);

    const [clientSecret, setClientSecret] = useState('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const [isLoadingClientSecret, setIsLoadingClientSecret] = useState(true);

    const [bookingReference, setBookingReference] = useState<string | null>(null);
    const [bookingHash, setBookingHash] = useState<string | null>(null);

    const [stripePaymentId, setStripePaymentId] = useState<string | null>(null);
    const [stripePaymentMethod, setStripePaymentMethod] = useState<string | null>(null);

    const [paymentOption, setPaymentOption] = useState<string>('minimum');

    const [isPhoneValid, setIsPhoneValid] = useState(true);
    const [phoneError, setPhoneError] = useState<string | null>(null);

    const [recurringSlotError, setRecurringSlotError] = useState<string | null>(null);
    const [showBookingSummary, setShowBookingSummary] = useState(true);

    const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);

    const [showGiftCardInput, setShowGiftCardInput] = useState(false);
    const [giftCardNumber, setGiftCardNumber] = useState('');
    const [appliedGiftCard, setAppliedGiftCard] = useState<{ number: string; balance: number } | null>(null);
    const [isCheckingGiftCard, setIsCheckingGiftCard] = useState(false);
    const [giftCardError, setGiftCardError] = useState<string | null>(null);

    const [rewardsEnabled, setRewardsEnabled] = useState(false);
    const [useRewardsBalance, setUseRewardsBalance] = useState(false);

    const [showProcessingDialog, setShowProcessingDialog] = useState(false);
    const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

    const [multipleBooking, setMultipleBooking] = useState(true);
    const [additionalBookings, setAdditionalBookings] = useState<AdditionalBooking[]>([]);
    const [showAddBooking, setShowAddBooking] = useState(false);
    const [multipleBookingsEnabled, setMultipleBookingsEnabled] = useState(false);

    const [slotTimes, setSlotTimes] = useState<Record<number, string>>({});

    const [dateAvailability, setDateAvailability] = useState<CalendarDateAvailability[]>([]);
    const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

    const ukPartialPostcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]$/i;

    const stripe = useStripe();
    const elements = useElements();

    const paymentElementOptions = {
        layout: "tabs"
    }

    const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            fullName: user?.name || "",
            phoneNumber: user?.phone_number || "",
            email: user?.email || "",
            numberOfDogs: "1",
            dogs: [{ name: '', breed: '' }],
            dogsCertification: true,
            penId: "",
            address: {
                line1: '',
                line2: '',
                city: '',
                county: '',
                postcode: '',
                country: 'GB',
            },
            bookingDate: new Date(),
            bookingLength: '1',
            selectedSlots: [],
            paymentType: 'card',
            discountCode: '',
            isRecurring: false,
            recurringDuration: '',
            termsAccepted: false,
            newsletter: true
        },
    });

    // watchers
    const penId = watch('penId');
    const paymentMethodId = watch('paymentMethodId');
    const numberOfDogs = watch('numberOfDogs');
    const bookingDate = watch('bookingDate');
    const bookingLength = watch('bookingLength');
    const postcode = watch('address.postcode');
    const fullName = watch('fullName');
    const email = watch('email');
    const watchedFields = watch();

    // effects
    useEffect(() => {
        if (email) {
            const suggestion = getEmailSuggestion(email);
            setEmailSuggestion(suggestion);
        } else {
            setEmailSuggestion(null);
        }
    }, [email]);

    useEffect(() => {
        const fetchBreeds = async () => {
            try {
                const response = await axios.get<Breed[]>('/api/breeds');
                setBreeds(response.data);
            } catch (error) {
                console.error('Error fetching breeds:', error);
            }
        };

        fetchBreeds();
    }, []);

    useEffect(() => {
        const dogCount = parseInt(numberOfDogs || '0', 10);
        setDogs(Array(dogCount).fill({ name: '', breed: '' }));
    }, [numberOfDogs]);

    useEffect(() => {
        setValue('dogs', dogs);
    }, [dogs, setValue]);

    useEffect(() => {
        setValue('dogsCertification', dogsCertification);
    }, [dogsCertification, setValue]);

    useEffect(() => {
        setPayAllRecurring(paymentOption === 'all');
    }, [paymentOption]);

    useEffect(() => {
        if (searchTerm.length > 1) {
            if (ukPartialPostcodeRegex.test(searchTerm.trim())) {
                console.log('matches');
                setPostcodeWarning("Looks like you're starting with your postcode, try typing the first line of your address instead");
                setSuggestions([]);
                setShowSuggestions(false);
            } else {
                console.log('No match');
                setPostcodeWarning('');
                const fetchSuggestions = async () => {
                    try {
                        const response = await axios.get(
                            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchTerm)}.json`,
                            {
                                params: {
                                    access_token: mapboxToken,
                                    types: 'address',
                                    country: 'GB',
                                },
                            }
                        );
                        setSuggestions(response.data.features);
                        setShowSuggestions(true);
                    } catch (error) {
                        console.error('Error fetching suggestions:', error);
                    }
                };

                const debounceTimer = setTimeout(fetchSuggestions, 300);
                return () => clearTimeout(debounceTimer);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
            setPostcodeWarning('');
        }
    }, [searchTerm, mapboxToken]);

    useEffect(() => {
        if (field.pens && field.pens.length > 0) {
            setValue('penId', field.pens[0].id);
            setSelectedPenId(field.pens[0].id);
        }
    }, [field.pens, setValue]);

    useEffect(() => {
        if (user && user.id) {
            fetchPaymentMethods();
        }
    }, [user]);

    useEffect(() => {
        if (penId && bookingDate && selectedSlots.length > 0) {
            if (paymentType === 'saved_method' && paymentMethodId) {
                fetchBookingSummary();
            } else if (paymentType !== 'saved_method') {
                fetchBookingSummary();
            } else {
                setBookingSummary(null);
            }
        } else if (additionalBookings.length > 0) {
            fetchBookingSummary();
        } else {
            setBookingSummary(null);
        }
    }, [penId, bookingDate, selectedSlots, paymentType, paymentMethodId, recurringCycle, recurringFrequency, payAllRecurring, recurringSlots, appliedGiftCard, useRewardsBalance, additionalBookings]);

    useEffect(() => {
        fetchSetupIntent(email);
    }, [showAddCardForm]);

    useEffect(() => {
        const checkSamsungMobile = () => {
            const userAgent = navigator.userAgent;
            const isSamsung = /SamsungBrowser/i.test(userAgent);
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
            setIsSamsungMobile(isSamsung && isMobile);
        };

        checkSamsungMobile();
    }, []);

    useEffect(() => {
        if (bookingSummary) {
            const allSessionsPrice = bookingSummary.price * recurringSlots.length;
            const initialFourSessionsPrice = bookingSummary.price * Math.min(4, recurringSlots.length);

            setTotalPrice(allSessionsPrice);
            setInitialPaymentPrice(initialFourSessionsPrice);
        }
    }, [bookingSummary, recurringSlots]);

    useEffect(() => {
        if (isRecurring && recurringSlots.length === 0) {
            setRecurringSlotError("You have selected a recurring booking, but you have not finalized your slots. Please click the 'Check Availability for Recurring Slots' button above to continue.");
            setShowBookingSummary(false);
        } else {
            setRecurringSlotError(null);
            setShowBookingSummary(true);
        }
    }, [isRecurring, recurringSlots]);

    useEffect(() => {
        if (isRecurringFinalized) {
            resetRecurringBooking();
        }
    }, [bookingDate, bookingLength, selectedSlots]);

    useEffect(() => {
        fetchMonthAvailability(bookingDate);
    }, [bookingDate]);

    const rewardsBalanceAmount = useMemo(() => {
        if (!user?.rewardsBalance || !bookingSummary?.price) return 0;
        return Math.min(user.rewardsBalance / 100, bookingSummary.price);
    }, [user?.rewardsBalance, bookingSummary?.price]);

    useEffect(() => {
        if (isRecurring && isDiscountApplied) {
            handleRemoveDiscount();
        }
    }, [isRecurring]);

    const resetRecurringBooking = () => {
        setIsRecurring(false);
        setRecurringCycle(null);
        setRecurringFrequency(null);
        setRecurringSlots([]);
        setIsRecurringFinalized(false);
        setRecurringBookingSummary('');
        setPayAllRecurring(false);
        setPaymentOption('minimum');
    };

    const handleRemoveRecurringBooking = () => {
        resetRecurringBooking();
    };

    const getEmailSuggestion = (email: string): string | null => {
        const commonDomains = [
            'gmail.com',
            'googlemail.com',
            'yahoo.com', 
            'hotmail.com', 
            'outlook.com', 
            'icloud.com',
            'mac.com',
            'me.com',
            'aol.com',
            'live.com',
            'msn.com',
            'btinternet.com',
            'sky.com',
            'virginmedia.com',
            'ntlworld.com',
            'blueyonder.co.uk',
            'talktalk.net',
        ];

        const [localPart, domain] = email.split('@');

        if (!domain) return null;

        // Check if the domain is an exact match first
        if (commonDomains.includes(domain.toLowerCase())) {
            return null;
        }

        for (const commonDomain of commonDomains) {
            if (domain.length > 3 &&
                commonDomain.startsWith(domain.slice(0, 3)) &&
                domain.toLowerCase() !== commonDomain) {
                return `${localPart}@${commonDomain}`;
            }
        }

        return null;
    };

    // Format gift card number with dashes
    const formatGiftCardNumber = (value: string) => {
        // Remove all non-digits
        const cleanValue = value.replace(/[^0-9]/g, '');

        // Split into groups of 3 and join with dashes
        const groups = cleanValue.match(/.{1,3}/g) || [];
        return groups.slice(0, 4).join('-');
    };

    // Mask gift card number for display
    const maskGiftCardNumber = (number: string) => {
        return number.replace(/\d/g, '*').replace(/[^-]/g, '*');
    };

    // add multiple bookings
    const handleAddToBooking = () => {
        const newBooking = {
            date: bookingDate,
            length: bookingLength,
            slots: selectedSlots,
            penId: penId
        };
        setAdditionalBookings([...additionalBookings, { ...newBooking, slotLength: bookingLength, selectedSlots: selectedSlots }]);

        // Reset for next booking
        setValue('bookingDate', new Date());
        setValue('bookingLength', '1');
        setValue('selectedSlots', []);
        setSelectedSlots([]);
    };

    const handleEnableMultipleBookings = () => {
        // Convert current booking to first booking in multiple bookings
        const currentBooking = {
            date: bookingDate,
            slotLength: bookingLength,
            selectedSlots: selectedSlots,
            penId: penId
        };

        setAdditionalBookings([currentBooking]);
        setMultipleBooking(true);

        // Reset current selection fields
        setValue('bookingDate', new Date());
        setValue('bookingLength', '1');
        setValue('selectedSlots', []);
        setSelectedSlots([]);
    };

    // Handle gift card input change
    const handleGiftCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const formattedValue = formatGiftCardNumber(value);
        setGiftCardNumber(formattedValue);
    };

    // Handle paste event
    const handleGiftCardPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');

        // Remove any existing dashes, spaces, or other non-digit characters
        const cleanNumber = pastedText.replace(/[^0-9]/g, '');

        // Format the clean number
        const formattedValue = formatGiftCardNumber(cleanNumber);
        setGiftCardNumber(formattedValue);
    };

    // Handle keydown to prevent invalid input
    const handleGiftCardKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Handle Ctrl+V
        if (e.ctrlKey && e.key.toLowerCase() === 'v') {
            e.preventDefault();
            navigator.clipboard.readText().then(pastedText => {
                const cleanNumber = pastedText.replace(/[^0-9]/g, '');
                const formattedValue = formatGiftCardNumber(cleanNumber);
                setGiftCardNumber(formattedValue);
            });
            return;
        }

        // Original keyDown handling for manual input
        // Allow: backspace, delete, tab, escape, enter, dash
        if ([46, 8, 9, 27, 13, 189, 109].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            // Allow: home, end, left, right
            (e.keyCode >= 35 && e.keyCode <= 39)) {
            return;
        }
        // Only allow numbers
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) &&
            (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
        }
    };

    const handleEmailSuggestion = () => {
        if (emailSuggestion) {
            setValue('email', emailSuggestion);
            setEmailSuggestion(null);
        }
    };

    const dismissSuggestion = () => {
        setEmailSuggestion(null);
    };

    const validateUKPhoneNumber = (phone: string): { isValid: boolean; message: string | null } => {
        // If empty, return valid with no message
        if (phone === '') {
            return { isValid: true, message: null };
        }

        // If contains non-numeric characters
        if (/[^0-9]/.test(phone)) {
            return {
                isValid: false,
                message: "Please enter numbers only - no spaces or special characters"
            };
        }

        // Check for valid starting patterns
        if (!/^(0|44)?7/.test(phone)) {
            return {
                isValid: false,
                message: "UK mobile numbers should start with '07' or '447'"
            };
        }

        // If it starts correctly, only show format error if it's long enough to be a full number
        if (phone.length >= 11) {
            const ukPhoneRegex = /^(0|44)?7\d{9}$/;
            return {
                isValid: ukPhoneRegex.test(phone),
                message: ukPhoneRegex.test(phone) ? null : "This doesn't look like a valid UK mobile number"
            };
        }

        // If it starts correctly but isn't complete yet, show a helper message
        return {
            isValid: true,
            message: "Continue entering your mobile number..."
        };
    };


    //handlers
    const handleGiftCardSubmit = async () => {
        if (!giftCardNumber.trim()) return;

        setIsCheckingGiftCard(true);
        setGiftCardError(null);

        try {
            const response = await axios.post('/api/user/cards', {
                card_number: giftCardNumber
            });

            if (response.data && response.data.balance > 0) {
                setAppliedGiftCard({
                    number: giftCardNumber,
                    balance: response.data.balance
                });
                setValue('giftCardNumber', giftCardNumber);
                setShowGiftCardInput(false);
                setGiftCardNumber('');
            } else {
                setGiftCardError('Invalid gift card or zero balance.');
            }
        } catch (error: any) {
            setGiftCardError(error.response?.data?.message || 'Failed to validate gift card.');
        } finally {
            setIsCheckingGiftCard(false);
        }
    };

    const removeGiftCard = () => {
        setAppliedGiftCard(null);
        setValue('giftCardNumber', '');
        setBookingSummary(null);
        fetchBookingSummary();
    };


    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawInput = e.target.value;
        // Strip non-numeric characters
        const phoneNumber = rawInput.replace(/[^0-9]/g, '');

        // Update the form value
        setValue('phoneNumber', phoneNumber);

        // Get validation result
        const validation = validateUKPhoneNumber(phoneNumber);

        // Update error state
        setPhoneError(validation.message);
    };


    const handleSaveDogInfo = () => {
        setDialogOpen(false);

        // Check if any selected breed is banned
        const hasBannedBreed = dogs.some(dog => {
            const selectedBreed = breeds.find(breed => breed.id.toString() === dog.breed);
            return selectedBreed && selectedBreed.banned === 1;
        });

        if (hasBannedBreed) {
            setBannedBreedDialogOpen(true);
        }
    };

    const handlePenSelection = (e: React.MouseEvent, penId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedPenId(penId);
        setIsAnyPenSelected(false);
        setValue('penId', penId);
        return false;
    };

    const handleAnyPenSelection = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedPenId(null);
        setIsAnyPenSelected(true);
        setValue('penId', 'any');
        return false;
    };

    const handleSuggestionSelect = (feature: any) => {
        const context = feature.context || [];
        const street = context.find((item: any) => item.id.startsWith('street_name'))?.text || '';
        const postcode = context.find((item: any) => item.id.startsWith('postcode'))?.text || '';
        const city = context.find((item: any) => item.id.startsWith('place'))?.text || '';
        const county = context.find((item: any) => item.id.startsWith('region'))?.text || '';
        const country = context.find((item: any) => item.id.startsWith('country'))?.text || 'GB';

        const newAddress: Address = {
            line1: feature.address + street || '',
            line2: '',
            city,
            county,
            postcode,
            country,
        };

        Object.entries(newAddress).forEach(([key, value]) => {
            setValue(`address.${key as keyof Address}`, value);
        });

        setSearchTerm('');
        setShowSuggestions(false);
        setIsManualEntry(true);
    };

    const handleSlotsSelect = (slotIds: number[]) => {
        setSelectedSlots(slotIds);
        setValue('selectedSlots', slotIds);
    };

    const handlePaymentMethodSelect = (value: string) => {
        if (value === 'add_new') {
            setIsAddingCard(true);
            setValue('paymentType', 'card');
            setValue('paymentMethodId', '');  // Clear the paymentMethodId when adding a new card
        } else {
            setIsAddingCard(false);
            setValue('paymentType', 'saved_method');
            setValue('paymentMethodId', value);
        }
    };

    const handleAddNewCard = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) {
            setPaymentError('Stripe has not been initialized');
            return;
        }

        setIsProcessingPayment(true);
        setPaymentError(null);

        try {
            const { setupIntent, error } = await stripe.confirmCardSetup(setupIntentSecret, {
                payment_method: {
                    card: elements.getElement(CardElement)!,
                    billing_details: { name: newCardholderName },
                },
            });

            if (error) {
                throw new Error(error.message);
            }

            if (setupIntent?.status === 'succeeded') {
                const response = await axios.post('/api/user/methods', {
                    payment_method: setupIntent.payment_method,
                });
                setPaymentMethods([...paymentMethods, response.data.method]);
                setValue('paymentMethodId', response.data.method.id);
                setValue('paymentType', 'saved_method');
                setIsAddingCard(false);
                setShowAddCardForm(false);
                setIsCardAdded(true);
            }
        } catch (error: any) {
            setPaymentError(error.message);
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const handleRecurringSubmit = async () => {
        setIsCheckingAvailability(true);
        try {
            const response = await axios.post('/api/user/booking/recurring', {
                bookingDate: bookingDate,
                bookingLength: bookingLength,
                penId: penId,
                selectedSlots: selectedSlots,
                cycle: recurringCycle,
                frequency: recurringFrequency,
                // Add other necessary data like initial booking date, penId, etc.
            });
            setRecurringSlots(response.data.slots);
            setIsCheckingAvailability(false);
            setShowRecurringDialog(true);
        } catch (error) {
            setIsCheckingAvailability(false);
            console.error('Error fetching recurring slots:', error);
            // Handle error (e.g., show an error message to the user)
        }
    };

    const handlePayAllRecurringChange = (checked: boolean) => {
        setPayAllRecurring(checked);
        fetchBookingSummary();
    };

    const handleFinalizeSlots = () => {
        setShowRecurringDialog(false);
        setIsRecurringFinalized(true);

        const sessionCount = recurringSlots.length;
        const recurringType = recurringCycle === 'daily' ? 'days' :
            recurringCycle === 'weekly' ? 'weeks' : 'months';
        const recurringDuration = recurringFrequency;

        setRecurringBookingSummary(
            `We will book ${sessionCount} sessions for the next ${recurringDuration} ${recurringType}. ` +
            `You will be billed for all session that fall due within the next 4 days, all other sessions will be to your payment card 4 days before the session.`
        );
    };

    const handleEditRecurringBooking = () => {
        setShowRecurringDialog(true);
    };

    const handleSelectAlternative = (index: number) => {
        setEditingSlotIndex(index);
        setAlternativeDate(null);
        setAlternativeSlots([]);
    };

    const handleConfirmNewSlot = () => {
        if (editingSlotIndex !== null && alternativeDate && alternativeSlots.length > 0) {
            const updatedSlots = [...recurringSlots];
            updatedSlots[editingSlotIndex] = {
                ...updatedSlots[editingSlotIndex],
                date: format(alternativeDate, 'yyyy-MM-dd'),
                slot_ids: alternativeSlots,
            };
            setRecurringSlots(updatedSlots);
            setEditingSlotIndex(null);
            setAlternativeDate(null);
            setAlternativeSlots([]);
        }
    };

    const handleApplyDiscount = async () => {
        setIsApplyingDiscount(true);
        setDiscountError(null);

        try {
            const response = await axios.post('/api/user/discount', { code: discountCode });

            if (response.data && response.data.code) {
                // Update the discount code in the form data
                setValue('discountCode', discountCode);

                // Fetch updated booking summary with the discount applied
                await fetchBookingSummary();
                setIsDiscountApplied(true);
            } else {
                if(response.data && response.data.message) {
                    setDiscountError(response.data.message);
                } else {
                    setDiscountError('Invalid discount code.');
                }
                setIsDiscountApplied(false);
            }
        } catch (error) {
            console.error('Error applying discount:', error);
            setDiscountError('Sorry, this discount code is not valid.');
            setIsDiscountApplied(false);
        } finally {
            setIsApplyingDiscount(false);
        }
    };

    const handleRemoveDiscount = () => {
        setDiscountCode('');
        setIsDiscountApplied(false);
        setValue('discountCode', '');
        fetchBookingSummary(); // Fetch updated summary without discount
    };


    // functions
    const switchToLookupMode = () => {
        setIsManualEntry(false);
        setSearchTerm('');
        setShowSuggestions(false);
    };

    const fetchSetupIntent = async (email: string) => {
        setIsLoadingIntent(true);
        setError(null);
        try {
            const response = await axios.post('/api/user/methods/intent', {
                email,
            });
            setSetupIntentSecret(response.data.token);
        } catch (err) {
            setError('Failed to initialize payment setup. Please try again.');
        } finally {
            setIsLoadingIntent(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            const response = await axios.get('/api/user/methods');
            setPaymentMethods(response.data || []);
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            setPaymentMethods([]);
        }
    };

    const fetchBookingSummary = async () => {
        setIsFetchingPrice(true);
        setBookingError(null);
        try {
            const response = await axios.post('/api/price', {
                penId,
                bookingDate,
                selectedSlots,
                paymentType,
                numberOfDogs,
                paymentMethodId,
                discountCode: watch('discountCode'),
                giftCardNumber: appliedGiftCard?.number,
                isRecurring,
                recurringDuration: recurringFrequency,
                recurringSlots,
                payAllRecurring,
                useRewardsBalance,
                multipleBooking,
                additionalBookings, // Add this line
            });
            setBookingSummary(response.data);
        } catch (error) {
            console.error('Error fetching booking summary:', error);
            setBookingError('Failed to fetch booking summary. Please try again.');
        } finally {
            setIsFetchingPrice(false);
        }
    };

    const handleRetryBooking = () => {
        setErrorDialogOpen(false);
        //window.location.reload();
    };

    const handlePayButtonClick = () => {
        handleSubmit(onSubmit)();
    };

    const onSubmit = async (data: FormData) => {
        console.log("onSubmit function called", data);
        setIsProcessingPayment(true);
        setBookingError(null);

        try {
            console.log("Sending booking data to API");
            // Send the booking data to your API
            const response = await axios.post('/api/book', data);
            console.log("API response received", response.data);

            // If successful, redirect to the thank you page
            if (response.data && response.data.booking && response.data.booking.hash) {
                console.log("Redirecting to thank you page");
                window.location.href = `/booking/${response.data.booking.hash}/thanks`;
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error: any) {
            console.error('Booking error:', error);
            setBookingError(error.response?.data?.message || error.message || 'An error occurred while processing your booking. Please try again.');
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const handlePaymentTypeSelect = (type: string) => {
        setPaymentType(type);
        switch (type) {
            case 'card':
                setPaymentMessage("You will be redirected to a payment form once you place your order.");
                setShowAddCardForm(false);
                setValue('paymentType', 'card');
                setValue('paymentMethodId', '');  // Clear the paymentMethodId
                setShowSavedPaymentMethods(false);  // Hide saved payment methods
                break;
            case 'google':
                setPaymentMessage("Paid by Google Pay");
                setShowAddCardForm(false);
                // Placeholder handler for Google Pay
                console.log("Google Pay selected");
                break;
            case 'apple':
                setPaymentMessage("Paid by Apple Pay");
                setShowAddCardForm(false);
                // Placeholder handler for Apple Pay
                console.log("Apple Pay selected");
                break;
            case 'add_new':
                setPaymentMessage("");
            default:
                setPaymentMessage("");
        }
    };

    const handlePayment = async () => {
        if (!stripe || !elements) {
            setBookingError("Stripe has not been initialized.");
            return;
        }

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/booking/confirm`,
            },
            redirect: 'if_required',
        });

        if (paymentIntent && paymentIntent?.status === "requires_action") {
            const { error: actionError, paymentIntent: confirmedIntent } =
                await stripe.handleCardAction(paymentIntent.client_secret || '');

            if (actionError) {
                setBookingError(actionError.message || "An error occurred during payment.");
            }

            if(paymentIntent && paymentIntent.next_action && paymentIntent.next_action.type === 'redirect_to_url') {
                var action = paymentIntent.next_action;
                window.location.href = action.redirect_to_url?.url || '';
            }
        }

        if (error) {
            setBookingError(error.message || "An error occurred during payment.");
        } else if (paymentIntent && paymentIntent.status === "succeeded") {
            setStripePaymentId(paymentIntent.id);
        } else {
            setBookingError("Unexpected payment result. Please try again.");
        }
    };

    const handlePayAllUpfront = () => {
        setPaymentOption('all');
        setPayAllRecurring(true);
    };

    const handlePaymentComplete = async (paymentIntentId: string) => {
        setPaymentIntentId(paymentIntentId);
        setIsPaymentDialogOpen(false);
        setShowProcessingDialog(true);
    };


    const makeBooking = async () => {
        if(isMakingBooking) return;

        if (!watch('termsAccepted')) {
            setBookingError('You must accept the terms and conditions to proceed.');
            setErrorDialogOpen(true);
            return;
        }

        setIsMakingBooking(true);
        setProcessingBooking(true);

        let data = {
            ...watchedFields,
            dogs: dogs,
            dogsCertification: dogsCertification,
            selectedSlots: selectedSlots,
            giftCardNumber: appliedGiftCard?.number,
            discountCode: watch('discountCode'), // Include the discount code
            recurringSlots: recurringSlots, // Include the recurring slots
            payAllRecurring: payAllRecurring, // Include the payAllRecurring flag
            recurringCycle: recurringCycle, // Include the recurring cycle
            recurringFrequency: recurringFrequency, // Include the recurring frequency
            multipleBooking,
            additionalBookings, // Add this line
        };

        try {
            const response = await axios.post('/api/book', data);

            if(response.data.stripeClientSecret) {
                setBookingHash(response.data.booking.hash);
                setBookingReference(response.data.booking.reference);
                setClientSecret(response.data.stripeClientSecret);
                setIsPaymentDialogOpen(true);
            } else if(response.data && response.data.paymentLink) {
                window.location.href = response.data.paymentLink;
                setProcessingBooking(false);
            } else if (response.data && response.data.booking && response.data.booking.hash) {
                window.location.href = `/booking/${response.data.booking.hash}/thanks`;
                setProcessingBooking(false);
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error: any) {
            console.error('Booking error:', error);
            setBookingError(error.response?.data?.message || error.message || 'An error occurred while processing your booking. Please try again.');
            setProcessingBooking(false);
            setErrorDialogOpen(true);
        }

        setIsMakingBooking(false);
        setProcessingBooking(false);
    }

    const renderSelect = <
        TFieldValues extends FieldValues = FormData,
        TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
    >(
        name: TName,
        options: { value: string; label: string }[],
        placeholder: string,
        control: Control<TFieldValues>
    ) => {
        if (isSamsungMobile) {
            return (
                <Controller
                    name={name}
                    control={control}
                    render={({ field }) => (
                        <select
                            {...field}
                            className="w-full p-2 border border-gray-300 rounded-md"
                        >
                            <option value="">{placeholder}</option>
                            {options.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    )}
                />
            );
        }

        return (
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                            <SelectValue placeholder={placeholder} />
                        </SelectTrigger>
                        <SelectContent>
                            {options.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            />
        );
    };

    const fetchMonthAvailability = async (month: Date) => {
        setIsLoadingAvailability(true);
        try {
            const response = await axios.get('/api/dates/month', {
                params: {
                    month: format(month, 'yyyy-MM-dd')
                }
            });
            setDateAvailability(response.data);
        } catch (error) {
            console.error('Error fetching availability:', error);
        } finally {
            setIsLoadingAvailability(false);
        }
    };

    const getDateAvailabilityClass = (date: Date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const availability = dateAvailability.find(d => d.date === dateStr);
        
        if (!availability) return '';
        
        return cn(
            'hover:bg-gray-100',
            {
                'bg-green-100 hover:bg-green-200': availability.availability === 'plenty',
                'bg-yellow-100 hover:bg-yellow-200': availability.availability === 'limited',
                'bg-orange-100 hover:bg-orange-200': availability.availability === 'few',
                'bg-red-100 hover:bg-red-200': availability.availability === 'none'
            }
        );
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <form onSubmit={(e) => e.preventDefault()} className="bg-white p-4 rounded md:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="col-span-1 bg-gray-100 p-8 rounded">
                        <div className="col-span-2 mb-4">
                            <h2 id="booking" className="text-lg font-bold">Booking for {field.name}</h2>
                        </div>

                        {field.pens && field.pens.length > 1 && (
                            <>
                                <div className='col-span-2 my-4'>
                                    <h3 className="text-lg font-semibold">Field</h3>
                                    <p className="text-sm">This location has multiple fields, select which one you'd like.</p>
                                </div>
                                <div className="col-span-2 my-4">
                                    <div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                            {field.pens.map((pen) => (
                                                <Button
                                                    key={pen.id}
                                                    onClick={(e) => handlePenSelection(e, pen.id)}
                                                    className={cn(
                                                        "h-auto text-left text-black hover:text-white flex flex-col items-start p-4",
                                                        selectedPenId === pen.id ? "bg-brand text-white" : "bg-white"
                                                    )}
                                                >
                                                    <div className="flex items-center w-full mb-2">
                                                        <Trees className="w-5 h-5 mr-2" />
                                                        <span className="font-bold">{pen.name}</span>
                                                    </div>
                                                    <p className="text-sm">{pen.description}</p>
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-span-2 my-4">
                                    <Button
                                        onClick={handleAnyPenSelection}
                                        className={cn(
                                            "w-full h-auto text-left text-black hover:text-white flex items-center p-4",
                                            isAnyPenSelected ? "bg-brand text-white" : "bg-white"
                                        )}
                                    >
                                        <Trees className="w-5 h-5 mr-2" />
                                        <span className="font-bold">Any Field With Available Slots</span>
                                    </Button>
                                </div>
                            </>
                        )}

                        <Separator className='col-span-2 my-4' />

                        <div className='col-span-2'>
                            <h3 className="text-lg font-semibold">About You</h3>
                            <p className="text-sm">We need to know who's making the booking.</p>
                        </div>

                        <div className='col-span-2 my-4 md:col-span-2'>
                            <div className="grid w-full items-center gap-1.5">
                                <Label className='font-semibold' htmlFor="fullName">Full Name</Label>
                                <Controller
                                    name="fullName"
                                    control={control}
                                    render={({ field }) => <Input {...field} id="fullName" />}
                                />
                                {errors.fullName && <p className="text-red-500">{errors.fullName.message}</p>}
                            </div>
                        </div>
                        <div className='col-span-2'>
                            <div className="grid w-full items-center gap-1.5">
                                <Label className='font-semibold' htmlFor="email">Email Address</Label>
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <Input {...field} id="email" />
                                            {emailSuggestion && (
                                                <Alert className="mt-2 pr-8 relative">
                                                    <AlertDescription>
                                                        Did you mean <button type="button" onClick={handleEmailSuggestion} className="text-brand underline">{emailSuggestion}</button>?
                                                    </AlertDescription>
                                                    <button
                                                        type="button"
                                                        onClick={dismissSuggestion}
                                                        className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                                                        aria-label="Dismiss suggestion"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </Alert>
                                            )}
                                        </div>
                                    )}
                                />
                                {errors.email && <p className="text-red-500">{errors.email.message}</p>}
                            </div>
                        </div>

                        <Separator className='col-span-2 my-4' />
                        
                        <div className='col-span-2'>
                            <h3 className="text-lg font-semibold">Phone Number</h3>
                            <p className="text-sm ">It's important your phone number is correct, as it will be required to access the field. Enter your phone number carefully into the field below.</p>
                            <div className='mt-4'>
                                <div className='col-span-2 md:col-span-1'>
                                    <div className="grid w-full items-center gap-1.5">
                                        <Label className='font-semibold' htmlFor="phoneNumber">Phone Number</Label>
                                        <Controller
                                            name="phoneNumber"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    id="phoneNumber"
                                                    type='tel'
                                                    pattern="[0-9]*"
                                                    placeholder="07xxx xxxxxx"
                                                    className={cn(
                                                        phoneError && "border-red-500 focus-visible:ring-red-500",
                                                        !phoneError && field.value && "border-green-500 focus-visible:ring-green-500"
                                                    )}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        handlePhoneChange(e);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        const allowedKeys = [
                                                            'Backspace',
                                                            'Delete',
                                                            'ArrowLeft',
                                                            'ArrowRight',
                                                            'Tab',
                                                            'Home',
                                                            'End'
                                                        ];
                                                        if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                                                            e.preventDefault();
                                                            // Show error message when non-numeric key is pressed
                                                            setPhoneError("Please enter numbers only");
                                                        }
                                                    }}
                                                    onPaste={(e) => {
                                                        e.preventDefault();
                                                        const pastedText = e.clipboardData.getData('text');
                                                        const numbersOnly = pastedText.replace(/[^0-9]/g, '');
                                                        setValue('phoneNumber', numbersOnly);
                                                        // Validate pasted content
                                                        const validation = validateUKPhoneNumber(numbersOnly);
                                                        setPhoneError(validation.message);
                                                    }}
                                                />
                                            )}
                                        />
                                        {phoneError && (
                                            <p className="text-red-500 text-sm">{phoneError}</p>
                                        )}
                                        {errors.phoneNumber && <p className="text-red-500">{errors.phoneNumber.message}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Separator className='col-span-2 my-4' />

                        <div className='col-span-2'>
                            <h3 className="text-lg font-semibold">Dogs</h3>
                            <p className="text-sm">We need to know how many dogs you're bringing.</p>
                            <div className='mt-2'>
                                <Controller
                                    name="numberOfDogs"
                                    control={control}
                                    render={({ field }) => (
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Number of dogs" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                                    <SelectItem key={num} value={num.toString()}>
                                                        {num} {num === 1 ? ' Dog' : 'Dogs'}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                                {errors.numberOfDogs && <p className="text-red-500">{errors.numberOfDogs.message}</p>}
                            </div>
                            <div className="mt-4 flex items-center space-x-2">
                                <Checkbox id="dogsCertification" onCheckedChange={(checked) => setDogsCertification(checked as boolean)} checked={true} />
                                <Label htmlFor="dogsCertification">
                                    All dogs have been wormed, microchipped and vaccinated.
                                </Label>
                            </div>
                            {numberOfDogs && parseInt(numberOfDogs) < 5 && (
                                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                                    <DialogTrigger asChild>
                                        <Button variant="link" className="mt-2 p-0 text-brand underline">Click here if you'd like to tell us more about your Dogs.</Button>
                                    </DialogTrigger>
                                    <DialogContent className={cn(
                                        "sm:max-w-[425px]", // This is the default width
                                        "sm:max-w-[550px]"  // This is the new, wider width
                                    )}>
                                        <DialogHeader>
                                            <DialogTitle>Dog Information</DialogTitle>
                                        </DialogHeader>
                                        <DogInfoForm
                                            dogs={dogs}
                                            setDogs={setDogs}
                                            breeds={breeds}
                                            setCertification={setDogsCertification}
                                            onSave={handleSaveDogInfo}
                                        />
                                    </DialogContent>
                                </Dialog>
                            )}
                        </div>

                        <BannedBreedDialog
                            open={bannedBreedDialogOpen}
                            onClose={() => setBannedBreedDialogOpen(false)}
                        />

                        {errors.dogs && <p className="text-red-500 col-span-2">{errors.dogs.message}</p>}
                        {errors.dogsCertification && <p className="text-red-500 col-span-2">{errors.dogsCertification.message}</p>}

                        {(!user || (user.addresses && user.addresses.length === 0)) && (
                            <>
                                <Separator className='col-span-2 my-4' />
                                <div className='col-span-2'>
                                    <h3 className="text-lg font-semibold">Your Address</h3>
                                    <p className="text-sm">We need to know your home address, for insurance reasons.</p>

                                    {!isManualEntry && (
                                        <div className="mb-4 mt-2">
                                            <Label htmlFor="address-lookup">Find address</Label>
                                            <div className="relative">
                                                <Input
                                                    id="address-lookup"
                                                    type="text"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    placeholder="First line of your address..."
                                                    className="pr-10"
                                                />
                                                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            </div>
                                            {postcodeWarning && (
                                                <p className="mt-2 text-red-500 text-sm">{postcodeWarning}</p>
                                            )}
                                            {showSuggestions && suggestions.length > 0 && (
                                                <ul className="mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                                    {suggestions.map((suggestion) => (
                                                        <li
                                                            key={suggestion.id}
                                                            onClick={() => handleSuggestionSelect(suggestion)}
                                                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                                        >
                                                            {suggestion.place_name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}

                                    {isManualEntry && (
                                        <div className="space-y-4 my-4">
                                            <div>
                                                <Label htmlFor="line1">Address Line 1</Label>
                                                <Controller
                                                    name="address.line1"
                                                    control={control}
                                                    render={({ field }) => <Input {...field} id="line1" />}
                                                />
                                                {errors.address?.line1 && <p className="text-red-500">{errors.address.line1.message}</p>}
                                            </div>
                                            <div>
                                                <Label htmlFor="line2">Address Line 2</Label>
                                                <Controller
                                                    name="address.line2"
                                                    control={control}
                                                    render={({ field }) => <Input {...field} id="line2" />}
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="city">City</Label>
                                                <Controller
                                                    name="address.city"
                                                    control={control}
                                                    render={({ field }) => <Input {...field} id="city" />}
                                                />
                                                {errors.address?.city && <p className="text-red-500">{errors.address.city.message}</p>}
                                            </div>
                                            <div>
                                                <Label htmlFor="county">County</Label>
                                                <Controller
                                                    name="address.county"
                                                    control={control}
                                                    render={({ field }) => <Input {...field} id="county" />}
                                                />
                                                {errors.address?.county && <p className="text-red-500">{errors.address.county.message}</p>}
                                            </div>
                                            <div>
                                                <Label htmlFor="postcode">Postcode</Label>
                                                <Controller
                                                    name="address.postcode"
                                                    control={control}
                                                    render={({ field }) => <Input {...field} id="postcode" />}
                                                />
                                                {errors.address?.postcode && <p className="text-red-500">{errors.address.postcode.message}</p>}
                                            </div>
                                            <div>
                                                <Label htmlFor="country">Country</Label>
                                                <Controller
                                                    name="address.country"
                                                    control={control}
                                                    render={({ field }) => <Input {...field} id="country" />}
                                                />
                                                {errors.address?.country && <p className="text-red-500">{errors.address.country.message}</p>}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-4">
                                        {!isManualEntry && (
                                            <>
                                                <p className="text-sm text-gray-600 mb-2">Having Trouble? Click the button below to enter your address manually.</p>
                                                <Button
                                                    onClick={() => setIsManualEntry(true)}
                                                    type="button"
                                                    variant="outline"
                                                    className="w-full"
                                                >
                                                    Enter Address Manually
                                                </Button>
                                            </>
                                        )}
                                        {isManualEntry && (
                                            <Button
                                                onClick={switchToLookupMode}
                                                type="button"
                                                variant="outline"
                                                className="flex items-center"
                                            >
                                                <ArrowLeft className="w-4 h-4 mr-2" />
                                                Back to Address Lookup
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="col-span-1 bg-gray-100 p-8 rounded">
                        <div className='col-span-2 my-y'>
                            <h3 className="text-lg font-semibold">Your Slot</h3>
                            <p className="text-sm">When would you like to come and for how long?</p>
                        </div>

                        <div className='col-span-2 my-4 md:col-span-1'>
                            <Label htmlFor="bookingDate">Booking Date</Label>
                            <Controller
                                control={control}
                                name="bookingDate"
                                render={({ field }) => (
                                    <DatePicker
                                        selected={field.value}
                                        onSelect={(date) => {
                                            field.onChange(date);
                                            setCalendarOpen(false);
                                        }}
                                        dateAvailability={dateAvailability}
                                        isOpen={calendarOpen}
                                        onOpenChange={setCalendarOpen}
                                        onMonthChange={fetchMonthAvailability}
                                    />
                                )}
                            />
                            {errors.bookingDate && <p className="text-red-500">{errors.bookingDate.message}</p>}
                        </div>

                        <div className='col-span-2 my-4 md:col-span-1'>
                            <Label htmlFor="bookingLength">Booking Length</Label>
                            {renderSelect("bookingLength", [
                                { value: "1", label: "30 minutes" },
                                { value: "2", label: "60 minutes" },
                                { value: "4", label: "120 minutes" },
                                { value: "6", label: "180 minutes" }
                            ], "Select booking length", control)}
                            {errors.bookingLength && <p className="text-red-500">{errors.bookingLength.message}</p>}
                        </div>

                        <div className='col-span-2 my-4'>
                            {isAnyPenSelected ? (
                                <MultiPenSlotSelector
                                    fieldSlug={field.slug}
                                    fieldId={field.id}
                                    date={bookingDate}
                                    slotLength={parseInt(bookingLength) || 1}
                                    onSlotsSelect={(slotIds, penId) => {
                                        setSelectedSlots(slotIds);
                                        setSelectedPenId(penId);
                                        setValue('selectedSlots', slotIds);
                                        setValue('penId', penId);
                                    }}
                                />
                            ) : (
                                <SlotSelector
                                    fieldSlug={field.slug}
                                    penId={selectedPenId}
                                    date={bookingDate}
                                    slotLength={parseInt(bookingLength) || 1}
                                    onSlotsSelect={(slotIds, slotTimeMap) => {
                                        setSelectedSlots(slotIds);
                                        setValue('selectedSlots', slotIds);
                                        let combimbed = {...slotTimes, ...slotTimeMap }
                                        setSlotTimes(combimbed);
                                    }}
                                />
                            )}

                            {multipleBooking && selectedSlots.length > 0 && (
                                <Button
                                    onClick={handleAddToBooking}
                                    className="w-full mt-4"
                                >
                                    Add To Booking
                                </Button>
                            )}

                            {multipleBooking && additionalBookings.length > 0 && (
                                <p className='text-sm text-center pt-2'>Select your date, time and slot length above to add to continue adding to your booking. All date and time slots we will book will be shown in the table below.</p>
                            )}

                            {errors.selectedSlots && <p className="text-red-500">{errors.selectedSlots.message}</p>}
                        </div>
                        
                        {isRecurringEnabled && additionalBookings.length < 1 && (
                            <>
                            {!isRecurringFinalized ? (
                            <div className="mt-4 col-span-2">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="isRecurring"
                                        checked={isRecurring}
                                        onCheckedChange={(checked) => setIsRecurring(checked as boolean)}
                                    />
                                    <Label htmlFor="isRecurring">Make this a recurring booking</Label>
                                </div>

                                {isRecurring && (
                                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <Label htmlFor="recurringCycle">Recurring Cycle</Label>
                                                    <Select value={recurringCycle || ''} onValueChange={setRecurringCycle}>
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue placeholder="Select cycle" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="daily">Daily</SelectItem>
                                                            <SelectItem value="weekly">Weekly</SelectItem>
                                                            <SelectItem value="monthly">Monthly</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label htmlFor="recurringFrequency">Recurring Frequency</Label>
                                                    <Select value={recurringFrequency || ''} onValueChange={setRecurringFrequency}>
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue placeholder="Select frequency" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {recurringCycle === 'daily' && Array.from({ length: 30 }, (_, i) => i + 1).map(num => (
                                                                <SelectItem key={num} value={num.toString()}>{num} {num === 1 ? 'Day' : 'Days'}</SelectItem>
                                                            ))}
                                                            {recurringCycle === 'weekly' && Array.from({ length: 52 }, (_, i) => i + 1).map(num => (
                                                                <SelectItem key={num} value={num.toString()}>{num} {num === 1 ? 'Week' : 'Weeks'}</SelectItem>
                                                            ))}
                                                            {recurringCycle === 'monthly' && Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                                                                <SelectItem key={num} value={num.toString()}>{num} {num === 1 ? 'Month' : 'Months'}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label htmlFor="paymentOption">Payment</Label>
                                                    <Select value={paymentOption} onValueChange={setPaymentOption}>
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue placeholder="Select payment option" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="minimum">Pay for minimum number of sessions</SelectItem>
                                                            <SelectItem value="all">Pay for everything upfront</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                    )}

                                    {isRecurring && recurringCycle && recurringFrequency && (
                                        <Button className="mt-4" onClick={handleRecurringSubmit}>
                                            Check Availability for Recurring Slots
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="bg-white col-span-2 p-4 rounded-lg shadow-sm">
                                        <p className="mb-4">{recurringBookingSummary}</p>
                                        <Button
                                            variant="link"
                                            className="p-0 h-auto text-brand"
                                            onClick={handleEditRecurringBooking}
                                        >
                                            Edit Recurring Booking Selections
                                        </Button>
                                    </div>
                                    <div className="mt-4 col-span-2">
                                        <Button
                                            className="bg-black text-white"
                                            onClick={handleRemoveRecurringBooking}
                                        >
                                            Remove Recurring Booking
                                        </Button>
                                    </div>
                                </>
                            )}
                            </>
                        )}

                        {selectedSlots.length > 0 && multipleBookingsEnabled && !isRecurring && !multipleBooking && !isAnyPenSelected && (
                            <div className='col-span-2'>
                                <Button
                                    onClick={handleEnableMultipleBookings}
                                    className="w-full mt-4"
                                >
                                    Add Additional Time Slots To Booking
                                </Button>
                            </div>
                        )}

                        {multipleBooking && additionalBookings.length > 0 && (
                        <div className='col-span-2'>
                            <div className="mt-4 bg-white rounded-lg p-4">
                                <h4 className="font-semibold mb-4">Your Time Slots</h4>
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="py-2 text-left">Date</th>
                                            <th className="py-2 text-left">Length</th>
                                            { field.pens && field.pens.length > 1 && (
                                                <th className="py-2 text-left">Field</th>
                                            )}
                                            <th className="py-2 text-center">Start Time</th>
                                            <th className="py-2 text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {additionalBookings.map((booking, index) => (
                                            <tr key={index} className="border-b">
                                                <td className="py-2">{format(booking.date, 'PPP')}</td>
                                                <td className="py-2">
                                                    {booking.slotLength === '1' ? '30 minutes' :
                                                        booking.slotLength === '2' ? '60 minutes' :
                                                        booking.slotLength === '4' ? '120 minutes' : '180 minutes'}
                                                </td>
                                                { field.pens && field.pens.length > 1 && (
                                                    <td className="py-2">{field.pens.find(pen => pen.id === booking.penId)?.name}</td>
                                                )}
                                                <td className="py-2 text-center">
                                                    <span>{slotTimes[booking.selectedSlots[0]]}</span>
                                                </td>
                                                <td className="py-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            const newBookings = [...additionalBookings];
                                                            newBookings.splice(index, 1);
                                                            setAdditionalBookings(newBookings);
                                                        }}
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                                <p className='text-sm text-center pt-2'>You will be billed for all time slots when you complete this booking.</p>
                        </div>
                        )}

                        <Separator className='col-span-2 my-4' />

                        <div className='col-span-2'>
                            <h3 className="text-lg font-semibold">Discount Code</h3>
                            <p className="text-sm">
                                {isRecurring
                                    ? "Discount codes may not be used on Recurring Bookings"
                                    : "If you have a discount code, enter it here."}
                            </p>
                            <div className="mt-2 flex space-x-2">
                                <Input
                                    value={discountCode}
                                    onChange={(e) => setDiscountCode(e.target.value)}
                                    placeholder="Enter discount code"
                                    readOnly={isDiscountApplied || isRecurring}
                                    disabled={isRecurring}
                                    className={cn(
                                        isDiscountApplied && "bg-green-100 text-green-800",
                                        isRecurring && "bg-gray-100 text-gray-500",
                                        "flex-grow"
                                    )}
                                />
                                {isDiscountApplied ? (
                                    <Button
                                        onClick={handleRemoveDiscount}
                                        variant="outline"
                                        disabled={isRecurring}
                                    >
                                        Remove
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleApplyDiscount}
                                        disabled={isApplyingDiscount || isRecurring}
                                    >
                                        {isApplyingDiscount ? 'Applying...' : 'Apply'}
                                    </Button>
                                )}
                            </div>
                            {discountError && !isRecurring && (
                                <Alert variant="destructive" className="mt-2">
                                    <AlertDescription>{discountError}</AlertDescription>
                                </Alert>
                            )}
                            {isDiscountApplied && !isRecurring && (
                                <Alert className="mt-2 text-green-700">
                                    <AlertDescription>Discount code applied successfully!</AlertDescription>
                                </Alert>
                            )}
                        </div>

                        <Separator className='col-span-2 my-4' />

                        {/* Replace the existing gift card section with this code */}
                        <div className='col-span-2'>
                            <h3 className="text-lg font-semibold">Gift Card</h3>
                            <p className="text-sm">Have a gift card? Enter the number below to apply it to your booking. Any unused balance will remain on your card for future use.</p>

                            {!appliedGiftCard ? (
                                <div className="mt-2 space-y-2">
                                    <div className="flex space-x-2">
                                        <Input
                                            value={giftCardNumber}
                                            onChange={handleGiftCardChange}
                                            onPaste={handleGiftCardPaste}
                                            onKeyDown={handleGiftCardKeyDown}
                                            placeholder="XXX-XXX-XXX-XXX"
                                            maxLength={15}
                                            className="flex-grow font-mono"
                                        />
                                        <Button
                                            onClick={handleGiftCardSubmit}
                                            disabled={isCheckingGiftCard || giftCardNumber.length < 14}
                                        >
                                            {isCheckingGiftCard ? 'Checking...' : 'Apply'}
                                        </Button>
                                    </div>
                                    <em className='text-xs mt-1'>Only enter the numbers of your gift card, the dashes will be added automatically.</em>
                                    {giftCardError && (
                                        <Alert variant="destructive">
                                            <AlertDescription>{giftCardError}</AlertDescription>
                                        </Alert>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-2">
                                    <div className="inline-flex items-center bg-green-400 text-green-800 px-3 py-1 rounded-full">
                                        <span className="mr-2">£{appliedGiftCard.balance.toFixed(2)} Gift Card: {appliedGiftCard.number}</span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-4 w-4 p-0 hover:bg-green-200"
                                            onClick={removeGiftCard}
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        

                        {rewardsEnabled && user?.rewardsBalance ? (
                            <>
                                <Separator className='col-span-2 mt-2' />
                                <div className='col-span-2'>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="useRewardsBalance"
                                            checked={useRewardsBalance}
                                            onCheckedChange={(checked) => {
                                                setUseRewardsBalance(checked as boolean);
                                                setValue('useRewardsBalance', checked as boolean);
                                            }}
                                        />
                                        <Label htmlFor="useRewardsBalance" className="text-sm">
                                            Use your Reward Points balance for £{rewardsBalanceAmount.toFixed(2)} off this booking.
                                        </Label>
                                    </div>
                                </div>
                            </>
                        ) : null}

                        <Separator className='col-span-2 my-4' />

                        <div className='col-span-2'>
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="termsAccepted"
                                    checked={watch('termsAccepted')}
                                    onCheckedChange={(checked) => setValue('termsAccepted', checked as boolean)}
                                />
                                <Label htmlFor="termsAccepted" className="text-sm">
                                    I accept the Grow Walkies terms and conditions. <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">Read terms</a>
                                </Label>
                            </div>
                            {errors.termsAccepted && <p className="text-red-500 text-sm mt-1">{errors.termsAccepted.message}</p>}
                        </div>

                        <div className='col-span-2 mt-2'>
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="newsletter"
                                    checked={watch('newsletter')}
                                    onCheckedChange={(checked) => setValue('newsletter', checked as boolean)}
                                />
                                <Label htmlFor="newsletter" className="text-sm">
                                    Keep me updated with Grow Walkies News
                                </Label>
                            </div>
                        </div>

                        <Separator className='col-span-2 mt-2 mb-2' />

                        {showBookingSummary && (
                            <div className='col-span-2'>
                                <h3 className="text-lg font-semibold mb-4">Booking Summary</h3>
                                {isFetchingPrice ? (
                                    <p>Calculating price...</p>
                                ) : bookingSummary ? (
                                    <div className="bg-white p-4 rounded-lg shadow">
                                        <table className="w-full">
                                            <tbody>
                                                {multipleBooking && additionalBookings.length > 0 ? (
                                                    <tr className="border-b">
                                                        <td className="py-2 font-semibold">Number of Sessions:</td>
                                                        <td className="py-2 text-right">{additionalBookings.length}</td>
                                                    </tr>
                                                ) : (
                                                    <>
                                                        <tr className="border-b">
                                                            <td className="py-2 font-semibold">Date:</td>
                                                            <td className="py-2 text-right">{bookingSummary.booking_date}</td>
                                                        </tr>
                                                        <tr className="border-b">
                                                            <td className="py-2 font-semibold">Time:</td>
                                                            <td className="py-2 text-right">{bookingSummary.booking_start_time}</td>
                                                        </tr>
                                                        <tr className="border-b">
                                                            <td className="py-2 font-semibold">Duration:</td>
                                                            <td className="py-2 text-right">{bookingSummary.booking_length}</td>
                                                        </tr>
                                                        <tr className="border-b">
                                                            <td className="py-2 font-semibold">Number of Dogs:</td>
                                                            <td className="py-2 text-right">{bookingSummary.number_of_dogs}</td>
                                                        </tr>
                                                    </>
                                                )}
                                                {multipleBooking && bookingSummary?.problem_sessions && bookingSummary.problem_sessions > 0 ? (
                                                    <tr className="border-b">
                                                        <td colSpan={2} className="py-2 text-red-500">Some sessions has issues and were removed from your booking.</td>
                                                    </tr>
                                                ) : null}
                                                {bookingSummary.card_used && (
                                                    <tr className="border-b">
                                                        <td className="py-2 font-semibold">Gift Card:</td>
                                                        <td className="py-2 text-right">Using £{bookingSummary.card_used} - £{bookingSummary.card_balance} remaining.</td>
                                                    </tr>
                                                )}
                                                {bookingSummary.discount && (
                                                    <tr className="border-b">
                                                        <td className="py-2 font-semibold">Discount:</td>
                                                        <td className="py-2 text-right">£{bookingSummary.discount}</td>
                                                    </tr>
                                                )}
                                                {bookingSummary.original_price && (
                                                    <tr className="border-b">
                                                        <td className="py-2 font-semibold">Original Price:</td>
                                                        <td className="py-2 text-right">£{bookingSummary.original_price}</td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td className="py-2 font-semibold text-lg">{bookingSummary.per_session_price > 0 ? 'Per Session Price' : 'Total Price'}:</td>
                                                    <td className="py-2 text-right font-bold text-lg">£{bookingSummary.per_session_price > 0 ? bookingSummary.per_session_price : bookingSummary.price}</td>
                                                </tr>
                                                {isRecurringEnabled && isRecurringFinalized && (
                                                    <tr>
                                                        <td className="py-2 font-semibold text-lg">
                                                            {isRecurringFinalized ? (payAllRecurring ? 'Total Price (All Sessions):' : 'Initial Payment (Due Sessions):') : 'Total Price:'}
                                                        </td>
                                                        <td className="py-2 text-right font-bold text-lg">
                                                            £{bookingSummary.price}
                                                        </td>
                                                    </tr>
                                                )}
                                                {isRecurringFinalized && !payAllRecurring && (
                                                    <tr>
                                                        <td colSpan={2} className="py-2 text-sm text-gray-600 italic">
                                                            Remaining sessions will be charged 4 days before each booking.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="italic text-gray-600">Please complete all required fields to see the booking summary.</p>
                                )}
                            </div>
                        )}

                        <Separator className='col-span-2 my-4' />

                        {penId && email && selectedSlots && paymentType !== 'card' && (
                            <div className="col-span-2">
                                {recurringSlotError && (
                                    <Alert variant="destructive" className="mb-4">
                                        <AlertDescription>{recurringSlotError}</AlertDescription>
                                    </Alert>
                                )}
                                <Button
                                    className='float-right'
                                    onClick={makeBooking}
                                    disabled={isProcessingPayment || processingBooking || (isRecurring && recurringSlots.length === 0)}
                                >
                                    {isProcessingPayment || processingBooking ? 'Processing...' : 'Confirm & Pay for Booking'}
                                </Button>
                            </div>
                        )}

                        {clientSecret && (
                            <PaymentDialog
                                isOpen={isPaymentDialogOpen}
                                onClose={() => setIsPaymentDialogOpen(false)}
                                clientSecret={clientSecret}
                                onPaymentComplete={handlePaymentComplete}
                                bookingReference={bookingHash || ''}
                            />
                        )}

                        <Dialog open={errorDialogOpen} onOpenChange={handleRetryBooking}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Booking Error</DialogTitle>
                                </DialogHeader>
                                <Alert variant="destructive">
                                    <AlertDescription>{bookingError}</AlertDescription>
                                </Alert>
                                <DialogFooter>
                                    <Button onClick={handleRetryBooking}>Retry Booking</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        <Dialog open={processingBooking}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Processing Your Booking</DialogTitle>
                                </DialogHeader>
                                <Alert>
                                    <AlertDescription>Processing...</AlertDescription>
                                </Alert>
                            </DialogContent>
                        </Dialog>

                        <Dialog open={showRecurringDialog} onOpenChange={setShowRecurringDialog}>
                            <DialogContent className="max-w-4xl">
                                <DialogHeader>
                                    <DialogTitle className="text-2xl font-bold mb-4">Recurring Slots</DialogTitle>
                                </DialogHeader>
                                <div className="mt-4 max-h-[70vh] overflow-y-auto pr-4">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="bg-gray-100">
                                                <th className="border p-2 text-left">Date</th>
                                                <th className="border p-2 text-left">Status</th>
                                                <th className="border p-2 text-left">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recurringSlots.map((slot, index) => (
                                                <React.Fragment key={index}>
                                                    <tr className={slot.slot_ids?.length ? 'bg-green-50' : 'bg-red-50'}>
                                                        <td className="border p-2">{slot.date} {slot.time}</td>
                                                        <td className="border p-2">
                                                            {slot.slot_ids?.length ? (
                                                                <span className="text-green-600 font-semibold">Available</span>
                                                            ) : (
                                                                <span className="text-red-600 font-semibold">Unavailable</span>
                                                            )}
                                                        </td>
                                                        <td className="border p-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleSelectAlternative(index)}
                                                            >
                                                                {slot.slot_ids?.length ? 'Edit Selection' : 'Select Alternative'}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                    {editingSlotIndex === index && (
                                                        <tr>
                                                            <td colSpan={5} className="border p-4">
                                                                <div className="bg-white rounded-lg p-4">
                                                                    <h3 className="text-lg font-semibold mb-4">Select Alternative</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        <div>
                                                                            <Label className="mb-2 block">Select Date</Label>
                                                                            <Calendar
                                                                                mode="single"
                                                                                selected={alternativeDate || undefined}
                                                                                onSelect={(date) => {
                                                                                    if (date) {
                                                                                        setAlternativeDate(date);
                                                                                        setAlternativeSlots([]);
                                                                                    }
                                                                                }}
                                                                                className="rounded-md border"
                                                                                disabled={(date) => {
                                                                                    const today = new Date();
                                                                                    today.setHours(0, 0, 0, 0);
                                                                                    return date < today;
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        {alternativeDate && (
                                                                            <div>
                                                                                <Label className="mb-2 block">Select Slot</Label>
                                                                                <SlotSelector
                                                                                    fieldSlug={field.slug}
                                                                                    penId={selectedPenId}
                                                                                    date={alternativeDate}
                                                                                    slotLength={parseInt(bookingLength) || 1}
                                                                                    onSlotsSelect={setAlternativeSlots}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <Button
                                                                        onClick={handleConfirmNewSlot}
                                                                        disabled={!alternativeDate || alternativeSlots.length === 0}
                                                                        className="mt-4 bg-brand hover:bg-brand-dark text-white"
                                                                    >
                                                                        Confirm New Slot
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <DialogFooter className="mt-4">
                                    <Button onClick={handleFinalizeSlots} className="bg-brand hover:bg-brand-dark text-white">
                                        Finalize Slots & Continue
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        <Dialog open={isCheckingAvailability} onOpenChange={() => { }}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Checking Slot Availability</DialogTitle>
                                </DialogHeader>
                                <p>Checking slot availability, this may take a few moments...</p>
                            </DialogContent>
                        </Dialog>

                        {paymentIntentId && (
                            <PaymentProcessingDialog
                                isOpen={showProcessingDialog}
                                paymentIntentId={paymentIntentId}
                                bookingHash={bookingHash || ''}
                                shouldRedirect={true}
                                onClose={() => setIsProcessingPayment(false)}
                            />
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default NewBookingForm;
